import React, { useCallback, useEffect, useState } from 'react';
import { Alert, App, Segmented, Table, Tag, Tooltip, Typography } from 'antd';

import { useAccess } from '../auth/AccessContext.jsx';
import ActionIcon from '../components/ActionIcon.jsx';

const { Text } = Typography;

/**
 * "Notifications you receive", on the profile page
 * (docs/notifications/implementation-guide.md 5.2a, 8.2).
 *
 * The list is the server's (GET notifications/preferences): every code the
 * person receives - through one of their roles (after the claim check), or as
 * the person a notice is about - with whether it is emailed, whether it is
 * switched on at all, and whether they muted it. The Mute switch mutes that
 * code for this person only: neither the item nor the email. A security
 * notice (an authenticator reset, a password change, a step-up lock) cannot
 * be muted, and says so.
 *
 * What a person receives is decided by their roles; an administrator changes
 * that under User Management. A mute only narrows it.
 */
const NotificationPreferences = () => {
  const { api } = useAccess();
  const { message } = App.useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { notifications } = await api.notifications.preferences();
      setRows(notifications);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (row, muted) => {
    setBusy(row.code);
    try {
      await api.notifications.setMuted(row.code, muted);
      setRows((prev) => prev.map((r) => (r.code === row.code ? { ...r, muted } : r)));
      message.success((muted ? 'Muted: ' : 'Turned back on: ') + row.label);
    } catch (e) {
      message.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  const columns = [
    { title: 'Section', dataIndex: 'group', key: 'group', width: 160 },
    {
      title: 'Notification',
      key: 'label',
      className: 'profile-notification-cell',
      width: 520,
      render: (_, r) => (
        <>
          <Text strong>{r.label}</Text>
          {r.via === 'SUBJECT' && <Tag style={{ marginInlineStart: 8 }}>about you</Tag>}
          {r.email && <Tag color="blue" style={{ marginInlineStart: 8 }}>email</Tag>}
          {!r.enabled && <Tag style={{ marginInlineStart: 8 }}>switched off</Tag>}
          <div><Text type="secondary" style={{ fontSize: 12 }}>{r.help}</Text></div>
        </>
      ),
    },
    {
      title: 'Receive',
      key: 'muted',
      className: 'profile-receive-cell',
      width: 200,
      render: (_, r) => (r.mutable
        ? (
          <Segmented
            className="notification-receive-toggle"
            options={[{ label: 'Yes', value: 'YES' }, { label: 'No', value: 'NO' }]}
            value={r.muted ? 'NO' : 'YES'}
            disabled={busy === r.code}
            onChange={(value) => toggle(r, value === 'NO')}
            aria-label={'Receive ' + r.label}
          />
        )
        : (
          <Tooltip title="A security notice is always sent.">
            <Segmented
              className="notification-receive-toggle"
              options={[{ label: 'Yes', value: 'YES' }, { label: 'No', value: 'NO' }]}
              value="YES"
              disabled
              aria-label={r.label + ' is always sent'}
            />
          </Tooltip>
        )),
    },
  ];

  return (
    <div className="panel profile-notifications-panel">
      <h3 className="panel-title">Notification</h3>
      <div className="permission-info-banner profile-info-banner">
        <ActionIcon name="info" />
        <span>
          Your roles determine which notifications you receive. An administrator can manage these under User Management. Switch a notification off to stop receiving it in the Console and by email. Notifications are kept for 7 days.
        </span>
      </div>
      {error && <Alert type="error" showIcon message={error} className="auth-alert" />}
      <Table className="profile-notifications-table" rowKey="code" size="small" loading={loading} columns={columns} dataSource={rows} pagination={false}
        tableLayout="fixed" locale={{ emptyText: 'Your roles subscribe to no notifications.' }} />
    </div>
  );
};

export default NotificationPreferences;
